
import {Component} from "vue-property-decorator";
import {mixins} from "vue-class-component";
import AuthMixin from "../../mixins/auth.mixin";
import {Api} from "shared/api";
import {IBrandManual} from "shared/entities/brand-manual";
import IconPicker from "@/components/icon-picker.vue";
import {UploadFileResponse} from "shared/api/routes/file";
import {AuthService} from "@/services/auth";
import {apiHandler} from "@/services/api-handler";
import FileUpload from "@/components/file-upload.vue";
import {CreateBrandManualRequest} from "shared/api/routes/brand-manual";

type NewValues<T = keyof IBrandManual> = Record<string, Partial<IBrandManual>>;

@Component({
    components: {FileUpload, IconPicker}
})
export default class ManageBrandManuals extends mixins(AuthMixin) {
    brandManuals: IBrandManual[] = [];
    activeChanges: Record<string, Record<string, boolean>> = {};
    newValues: NewValues = {};
    newBrand: CreateBrandManualRequest|null = null;

    get orderedBrandManuals() {
        if (this.newBrand) return [];
        return this.brandManuals.sort((a, b) => a.order - b.order);
    }

    async mounted() {
        await this.loadBrandManuals();
    }

    private async loadBrandManuals() {
        this.brandManuals = await apiHandler(() => Api.brandManual.Get());
        for (const brandManual of this.brandManuals) {
            this.activeChanges[brandManual.id] = {};
            this.newValues[brandManual.id] = {};
        }
    }

    async remove(brandManual: IBrandManual) {
        if (!confirm(`Are you sure you want to remove ${brandManual.title}`)) {
            return;
        }
        await apiHandler(() => Api.brandManual[brandManual.id].Delete());
        alert("Manual has been removed.");
        await this.loadBrandManuals();
    }

    canDecrease(brandManual: IBrandManual) {
        return brandManual.order > 1;
    }

    canIncrease(brandManual: IBrandManual) {
        return brandManual.order < this.brandManuals.reduce((o, bm) => bm.order > o ? bm.order : o, 0);
    }

    async decreaseOrder(brandManual: IBrandManual) {
        if (!this.canDecrease(brandManual)) return;
        const previous = this.orderedBrandManuals.filter(obm => obm.order === brandManual.order - 1);
        if (previous && previous.length >= 1) {
            await apiHandler(() => Api.brandManual[brandManual.id].Patch({order: ++previous[0].order}));
        }
        await Api.brandManual[brandManual.id].Patch({order: --brandManual.order});
    }

    async increaseOrder(brandManual: IBrandManual) {
        if (!this.canIncrease(brandManual)) return;
        const previous = this.orderedBrandManuals.filter(obm => obm.order === brandManual.order + 1);
        if (previous && previous.length >= 1) {
            await apiHandler(() => Api.brandManual[brandManual.id].Patch({order: --previous[0].order}));
        }
        await apiHandler(() => Api.brandManual[brandManual.id].Patch({order: ++brandManual.order}));
    }

    startFieldChange(brandManual: IBrandManual, field: keyof IBrandManual) {
        this.activeChanges[brandManual.id][field] = true;
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        this.newValues[brandManual.id][field] = brandManual[field];
        this.$forceUpdate();
    }

    endFieldChange(brandManual: IBrandManual, field: keyof IBrandManual) {
        this.activeChanges[brandManual.id][field] = false;
        this.$forceUpdate();
    }

    async changeIcon(brandManual: IBrandManual, icon: string) {
        const toChange = {icon};
        await apiHandler(() => Api.brandManual[brandManual.id].Patch(toChange));
        brandManual.icon = icon;
    }

    async changeField(brandManual: IBrandManual, field: keyof IBrandManual) {
        const toChange: Partial<IBrandManual> = {};
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        toChange[field] = this.newValues[brandManual.id][field];
        await apiHandler(() => Api.brandManual[brandManual.id].Patch(toChange));
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        brandManual[field] = this.newValues[brandManual.id][field];
        this.endFieldChange(brandManual, field);
    }

    async fileChanged(brandManual: IBrandManual, newFile: string) {
        await apiHandler(() => Api.brandManual[brandManual.id].Patch({file: newFile}));
        brandManual.file = newFile;
        alert("File Updated.");
    }

    startNew() {
        this.newBrand = {
            title: "",
            description: "",
            icon: "faQuestion",
            file: "",
            order: this.orderedBrandManuals.length + 1
        };
    }

    async saveNew() {
        if (this.newBrand == null) return;
        const newBrand = await apiHandler(() => Api.brandManual.Post(this.newBrand as CreateBrandManualRequest));
        this.newBrand = null;
        await this.loadBrandManuals();
    }

    async cancelNew() {
        this.newBrand = null;
    }
}

